import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Lock, Phone } from "lucide-react";
import { toast } from "sonner";
import { Btn, Field } from "@/components/kit";
import OtpStep from "@/components/OtpStep";
import AuthLayout from "@/components/AuthLayout";
import { api } from "@/lib/api";

export default function Forgot() {
  const nav = useNavigate();
  const pre = useLocation().state;
  const [step, setStep] = useState(pre?.verified ? "password" : "phone");
  const [phone, setPhone] = useState("");
  const [full, setFull] = useState(pre?.phone || "");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ password: "", confirm: "" });
  const [pwErr, setPwErr] = useState({});

  const send = async () => {
    if (phone.replace(/\D/g, "").length < 7) return setErr("Enter a valid mobile number.");
    setErr(null);
    setBusy(true);
    try {
      const p = `+91${phone.replace(/\D/g, "")}`;
      await api.post("/auth/send-otp", { phone: p });
      setFull(p);
      setStep("otp");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const verify = async (code) => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post("/auth/verify-otp", { phone: full, code });
      if (!r?.verified) throw new Error("Incorrect or expired code.");
      setStep("password");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    const next = {};
    if (pw.password.length < 6) next.password = "Password must be at least 6 characters.";
    if (pw.confirm !== pw.password) next.confirm = "Passwords do not match.";
    setPwErr(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { phone: full, password: pw.password });
      toast.success("Password updated. Please log in.");
      nav("/login");
    } catch (e) {
      setPwErr({ password: e.message });
    } finally {
      setBusy(false);
    }
  };
  const back = () => (step === "otp" ? setStep("phone") : step === "password" && !pre?.verified ? setStep("otp") : nav("/login"));

  return (
    <AuthLayout>
      <button className="-ml-2 mb-4 w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F1F5F9]" onClick={back} data-testid="forgot-back"><ChevronLeft className="w-6 h-6" strokeWidth={2.4} /></button>
      {step === "phone" && (
        <div className="flex flex-col gap-5">
          <div><h1 className="text-[28px] font-extrabold">Reset password</h1><p className="text-[15px] text-[#475569] mt-1">Enter your registered mobile number and we'll send you a verification code.</p></div>
          <Field label="Mobile Number" icon={Phone} inputMode="tel" placeholder="9876543210" value={phone} onChange={(e) => setPhone(e.target.value)} error={err} testid="forgot-phone" />
          <Btn onClick={send} loading={busy} testid="forgot-send-otp">Send OTP</Btn>
        </div>
      )}
      {step === "otp" && <OtpStep phone={full} onVerify={verify} verifying={busy} error={err} prefix="forgot" />}
      {step === "password" && (
        <div className="flex flex-col gap-5">
          <div><h1 className="text-[28px] font-extrabold">Create new password</h1><p className="text-[15px] text-[#475569] mt-1">Choose a new password for your account.</p></div>
          <Field label="New Password" icon={Lock} type="password" placeholder="At least 6 characters" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} error={pwErr.password} testid="forgot-password" />
          <Field label="Confirm Password" icon={Lock} type="password" placeholder="Re-enter password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} error={pwErr.confirm} testid="forgot-confirm" />
          <Btn onClick={save} loading={busy} testid="forgot-submit">Update Password</Btn>
        </div>
      )}
    </AuthLayout>
  );
}
